import React, { useState, useEffect, useRef } from 'react';
import mapboxgl, { MapMouseEvent } from 'mapbox-gl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapPin, faSearchLocation, faMagnifyingGlassLocation } from '@fortawesome/free-solid-svg-icons';
import { formatCoordinates } from '../../../utils/coordinateFormatters';
import './PlacenameTool.scss';

interface PlacenameToolProps {
  map: mapboxgl.Map;
}

type ToolMode = 'off' | 'lookup' | 'search';

interface PlacenameItem {
  name: string;
  latitude: number;
  longitude: number;
  distance?: number;
  concise?: { term: string };
  province?: { description: string };
}

const PlacenameTool: React.FC<PlacenameToolProps> = ({ map }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [mode, setMode] = useState<ToolMode>('off');
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<mapboxgl.Popup | null>(null);
  const searchPopupRef = useRef<mapboxgl.Popup | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Handle mode changes - set up click handler
  useEffect(() => {
    if (mode === 'off' || !map) return;

    // Change cursor to crosshair
    map.getCanvas().style.cursor = 'crosshair';

    const handleMapClick = async (e: MapMouseEvent) => {
      const { lng, lat } = e.lngLat;

      if (mode === 'lookup') {
        await performLookup(lat, lng);
        setMode('off');
      } else if (mode === 'search') {
        showSearchPopup(lat, lng);
        setMode('off');
      }
    };

    map.on('click', handleMapClick);

    return () => {
      map.off('click', handleMapClick);
      map.getCanvas().style.cursor = '';
    };
  }, [mode, map]);

  const performLookup = async (lat: number, lon: number) => {
    setIsLoading(true);

    // Show loading popup
    const loadingPopup = new mapboxgl.Popup({ closeButton: true, closeOnClick: false })
      .setLngLat([lon, lat])
      .setHTML('<div class="placename-loading"><i class="fa fa-spinner fa-spin"></i> Searching nearby...</div>')
      .addTo(map);

    try {
      const response = await fetch('/api/placename/lookup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ lat, lon, radius: 5 }),
      });

      const data = await response.json();
      loadingPopup.remove();

      if (data.success && data.items.length > 0) {
        showResultsOnMap(data.items, 'lookup');
      } else {
        showNoResultsPopup(lat, lon);
      }
    } catch (error) {
      console.error('Placename lookup error:', error);
      loadingPopup.remove();
      showErrorPopup(lat, lon, 'Failed to lookup placenames');
    } finally {
      setIsLoading(false);
    }
  };

  const showSearchPopup = (lat: number, lon: number) => {
    // Close any existing search popup
    if (searchPopupRef.current) {
      searchPopupRef.current.remove();
    }

    const container = document.createElement('div');
    container.className = 'placename-search-form';
    container.innerHTML = `
      <div class="search-header">Search this Province/Territory</div>
      <input type="text" id="placename-search-input" placeholder="Enter place name..." />
      <button id="placename-search-btn">Search</button>
    `;

    const popup = new mapboxgl.Popup({ closeButton: true, closeOnClick: false })
      .setLngLat([lon, lat])
      .setDOMContent(container)
      .addTo(map);

    searchPopupRef.current = popup;

    // Set up event handlers after popup is added
    setTimeout(() => {
      const input = document.getElementById('placename-search-input') as HTMLInputElement;
      const btn = document.getElementById('placename-search-btn');

      if (input && btn) {
        input.focus();

        const doSearch = async () => {
          const query = input.value.trim();
          if (!query) return;

          popup.remove();
          await performSearch(lat, lon, query);
        };

        btn.addEventListener('click', doSearch);
        input.addEventListener('keypress', (e) => {
          if (e.key === 'Enter') doSearch();
        });
      }
    }, 100);
  };

  const performSearch = async (lat: number, lon: number, query: string) => {
    setIsLoading(true);

    // Show loading popup
    const loadingPopup = new mapboxgl.Popup({ closeButton: true, closeOnClick: false })
      .setLngLat([lon, lat])
      .setHTML(`<div class="placename-loading"><i class="fa fa-spinner fa-spin"></i> Searching for "${query}"...</div>`)
      .addTo(map);

    try {
      const response = await fetch('/api/placename/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ lat, lon, query }),
      });

      const data = await response.json();
      loadingPopup.remove();

      if (data.success && data.items.length > 0) {
        showResultsOnMap(data.items, 'search');
      } else if (data.error === 'Location is not within Canada') {
        showErrorPopup(lat, lon, 'Location is not within Canada');
      } else {
        showNoResultsPopup(lat, lon, query);
      }
    } catch (error) {
      console.error('Placename search error:', error);
      loadingPopup.remove();
      showErrorPopup(lat, lon, 'Failed to search placenames');
    } finally {
      setIsLoading(false);
    }
  };

  const clearMarkers = () => {
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];
  };

  // Cleanup markers on unmount
  useEffect(() => {
    return () => {
      clearMarkers();
    };
  }, []);

  const showResultsOnMap = (items: PlacenameItem[], type: 'lookup' | 'search') => {
    // Clear any existing markers
    clearMarkers();

    // Close any existing popup
    if (popupRef.current) {
      popupRef.current.remove();
    }

    const maxItems = 50; // Allow more items when showing on map
    const displayItems = items.slice(0, maxItems);

    // Create bounds to fit all markers
    const bounds = new mapboxgl.LngLatBounds();

    displayItems.forEach((item, index) => {
      const coords = formatCoordinates(item.latitude, item.longitude);

      // Create marker element with pin and label
      const el = document.createElement('div');
      el.className = 'placename-marker';
      el.innerHTML = `
        <div class="placename-marker-pin"></div>
        <div class="placename-marker-label">
          <span class="marker-name">${item.name}</span>
          <span class="marker-type">${item.concise?.term || ''}</span>
        </div>
      `;

      // Create popup with coordinates
      const popup = new mapboxgl.Popup({ offset: 25, closeButton: true })
        .setHTML(`
          <div class="placename-marker-popup">
            <strong>${item.name}</strong><br/>
            <span class="popup-type">${item.concise?.term || ''}</span>
            ${item.distance ? `<br/><span class="popup-distance">${item.distance.toFixed(1)} km away</span>` : ''}
            <div class="popup-coords">
              <div class="coords-label">DDM:</div>
              <div class="coords-value">${coords.dmm}</div>
              <div class="coords-label">DD:</div>
              <div class="coords-value">${coords.dd}</div>
              <div class="coords-label">DMS:</div>
              <div class="coords-value">${coords.dms}</div>
            </div>
          </div>
        `);

      const marker = new mapboxgl.Marker({ element: el })
        .setLngLat([item.longitude, item.latitude])
        .setPopup(popup)
        .addTo(map);

      // Add click handler to zoom in
      el.addEventListener('click', () => {
        map.flyTo({
          center: [item.longitude, item.latitude],
          zoom: 14,
          duration: 1000
        });
      });

      markersRef.current.push(marker);
      bounds.extend([item.longitude, item.latitude]);
    });

    // Fit map to show all markers with padding
    if (displayItems.length > 0) {
      map.fitBounds(bounds, {
        padding: { top: 50, bottom: 50, left: 50, right: 50 },
        maxZoom: 12
      });
    }
  };

  const showNoResultsPopup = (lat: number, lon: number, query?: string) => {
    new mapboxgl.Popup({ closeButton: true, closeOnClick: true })
      .setLngLat([lon, lat])
      .setHTML(`<div class="placename-no-results">No places found${query ? ` for "${query}"` : ' nearby'}</div>`)
      .addTo(map);
  };

  const showErrorPopup = (lat: number, lon: number, message: string) => {
    new mapboxgl.Popup({ closeButton: true, closeOnClick: true })
      .setLngLat([lon, lat])
      .setHTML(`<div class="placename-error">${message}</div>`)
      .addTo(map);
  };

  const handleModeSelect = (newMode: ToolMode) => {
    setMode(newMode);
    setIsDropdownOpen(false);
  };

  const cancelMode = () => {
    setMode('off');
  };

  return (
    <div className="placename-tool map-control" ref={dropdownRef}>
      {mode === 'off' ? (
        <>
          <button
            className={`placename-button control-button ${isDropdownOpen ? 'active' : ''}`}
            title="Placename lookup/search"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            aria-expanded={isDropdownOpen}
            aria-haspopup="listbox"
          >
            <FontAwesomeIcon icon={faMapPin} />
          </button>

          {isDropdownOpen && (
            <div className="placename-dropdown" role="listbox">
              <button
                className="placename-dropdown-item"
                onClick={() => handleModeSelect('lookup')}
                role="option"
              >
                <FontAwesomeIcon icon={faSearchLocation} className="item-icon" />
                <span>Lookup (5km)</span>
              </button>
              <button
                className="placename-dropdown-item"
                onClick={() => handleModeSelect('search')}
                role="option"
              >
                <FontAwesomeIcon icon={faMagnifyingGlassLocation} className="item-icon" />
                <span>Search Prov/Terr</span>
              </button>
            </div>
          )}
        </>
      ) : (
        <button
          className="placename-button control-button active"
          title="Click map to select location (ESC to cancel)"
          onClick={cancelMode}
        >
          <FontAwesomeIcon icon={mode === 'lookup' ? faSearchLocation : faMagnifyingGlassLocation} />
        </button>
      )}
    </div>
  );
};

export default PlacenameTool;
